<script lang="ts" setup>
import VSelect from '@shared/form/VSelect/VSelect.vue';
import FieldRowV2 from '@shared/form/FieldRowV2/FieldRowV2.vue';
import FieldGroup from '@shared/form/FieldRowV2/FieldGroup.vue';
import { useId } from '@tools/composables/id';
import {
  useInstellingenHaalOfferteDefaultInstellingenAdviseurOp,
  useInstellingenWerkOfferteDefaultInstellingenAdviseurBij,
} from '@services/crm-public/orval-api/instellingen-api/instellingen-api';
import { CrmDomainDomainAdviseursDirectePostVerzending } from '@services/crm-public/orval-api/model/crmDomainDomainAdviseursDirectePostVerzending';
import { SUREbusinessCommonDomainPremietermijn } from '@services/crm-public/orval-api/model/sUREbusinessCommonDomainPremietermijn';
import { computed, ref, watch } from 'vue';
import { useClaims } from '@auth/useClaims';
import { useField, useForm } from 'vee-validate';
import { type CrmAdviseursGetAdviseursDto as Adviseur } from '@services/crm-internal/api';
import { useWatchErrorHandler } from '@errors/useErrorHandler';
import { type LabelValuePair } from '@shared/form/types';
import FadeSlideTransition from '@shared/animation/transitions/FadeSlideTransition/FadeSlideTransition.vue';
import FloatLabel from '@shared/form-v2/FloatLabel/FloatLabel.vue';
import { watchDebounced } from '@vueuse/core';
import VToggleSwitch from '@shared/component/VToggleSwitch/VToggleSwitch.vue';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import { useAdviseursGetHuidigeAdviseur } from '@services/crm-public/orval-api/adviseurs-api/adviseurs-api';
import KantoorDocumentenField from './components/KantoorDocumentenField.vue';

interface Props {
  adviseur?: Adviseur;
}

const props = defineProps<Props>();
const claims = useClaims();

const { data: ingelogdeAdviseur } = useAdviseursGetHuidigeAdviseur({
  query: {
    select: ({ result }) => result,
  },
});
const adviseurNummer = computed(() =>
  claims.value.isInternal ? props.adviseur?.adviseurnummer : ingelogdeAdviseur.value?.id,
);

interface AdviseurInstellingenFormValues {
  defaultPremietermijn?: SUREbusinessCommonDomainPremietermijn;
  prolongatiemail: boolean;
  defaultHvvDag: number | null;
  defaultHvvMaand: number | null;
  directePostVerzending?: CrmDomainDomainAdviseursDirectePostVerzending;
}

const { values, resetForm } = useForm<AdviseurInstellingenFormValues>();
const ids = useId('offerte-defaults-adviseur', [
  'default-premietermijn',
  'prolongatiemail-niet-versturen',
  'default-hvv-dag',
  'default-hvv-maand',
  'directe-post-verzending',
]);

const {
  data: adviseurInstellingen,
  error: adviseurInstellingenError,
  isSuccess: adviseurInstellingenOpgehaald,
} = useInstellingenHaalOfferteDefaultInstellingenAdviseurOp(
  computed(() => ({
    adviseurnummer: props.adviseur?.adviseurnummer,
  })),
  {
    query: {
      enabled: () => !claims.value.isInternal || !!props.adviseur?.adviseurnummer,
      select: ({ result }) => result,
    },
  },
);
const adviseurInstellingenVerborgen = ref(false);
function verbergAdviseurInstellingen() {
  adviseurInstellingenVerborgen.value = true;
}
useWatchErrorHandler(adviseurInstellingenError, { onForbiddenError: verbergAdviseurInstellingen });

watch(
  adviseurInstellingen,
  (nieuweAdviseurInstellingen) => {
    if (nieuweAdviseurInstellingen) {
      resetForm({
        values: {
          defaultPremietermijn: nieuweAdviseurInstellingen.defaultPremietermijn,
          prolongatiemail: !(nieuweAdviseurInstellingen.prolongatiemailNietVersturen ?? false),
          defaultHvvDag: nieuweAdviseurInstellingen.defaultHvvDag ?? null,
          defaultHvvMaand: nieuweAdviseurInstellingen.defaultHvvMaand ?? null,
          directePostVerzending: nieuweAdviseurInstellingen.directePostVerzending,
        },
      });
    }
  },
  { immediate: true },
);

const { mutate: werkAdviseurInstellingenBij, error: adviseurInstellingenOpslaanError } =
  useInstellingenWerkOfferteDefaultInstellingenAdviseurBij();

useWatchErrorHandler(adviseurInstellingenOpslaanError);

function save() {
  if (!adviseurInstellingenOpgehaald.value || adviseurInstellingenVerborgen.value) {
    return;
  }
  werkAdviseurInstellingenBij({
    params: { adviseurnummer: props.adviseur?.adviseurnummer },
    data: {
      defaultPremietermijn: values.defaultPremietermijn,
      prolongatiemailNietVersturen: !values.prolongatiemail,
      defaultHvvDag: values.defaultHvvDag,
      defaultHvvMaand: values.defaultHvvMaand,
      directePostVerzending: values.directePostVerzending,
    },
  });
}

watchDebounced(values, save, { debounce: 500, deep: true });

const defaultPremietermijnOptions: Array<LabelValuePair<SUREbusinessCommonDomainPremietermijn>> = [
  { label: 'Maand', value: SUREbusinessCommonDomainPremietermijn.Maand },
  { label: 'Kwartaal', value: SUREbusinessCommonDomainPremietermijn.Kwartaal },
  { label: 'Jaar', value: SUREbusinessCommonDomainPremietermijn.Jaar },
];

const directePostVerzendingOptions: Array<
  LabelValuePair<CrmDomainDomainAdviseursDirectePostVerzending>
> = [
  {
    label: 'Adviseur en relatie',
    value: CrmDomainDomainAdviseursDirectePostVerzending.AdviseurEnRelatie,
  },
  { label: 'Enkel adviseur', value: CrmDomainDomainAdviseursDirectePostVerzending.Adviseur },
  { label: 'Enkel relatie', value: CrmDomainDomainAdviseursDirectePostVerzending.Relatie },
];

const hvvDagOptions: Array<LabelValuePair<number | null>> = [
  { label: 'Geen voorkeur', value: null },
  ...Array.from({ length: 31 }, (_, i) => ({ label: String(i + 1), value: i + 1 })),
];

const hvvMaandOptions: Array<LabelValuePair<number | null>> = [
  { label: 'Geen voorkeur', value: null },
  { label: 'Januari', value: 1 },
  { label: 'Februari', value: 2 },
  { label: 'Maart', value: 3 },
  { label: 'April', value: 4 },
  { label: 'Mei', value: 5 },
  { label: 'Juni', value: 6 },
  { label: 'Juli', value: 7 },
  { label: 'Augustus', value: 8 },
  { label: 'September', value: 9 },
  { label: 'Oktober', value: 10 },
  { label: 'November', value: 11 },
  { label: 'December', value: 12 },
];

const { value: defaultPremietermijn, errorMessage: defaultpremietermijnError } = useField<
  SUREbusinessCommonDomainPremietermijn | undefined
>('defaultPremietermijn');
const { value: prolongatiemail } = useField<boolean>('prolongatiemail');
const { value: defaultHvvDag } = useField<number | null>('defaultHvvDag');
const { value: defaultHvvMaand } = useField<number | null>('defaultHvvMaand');
const { value: directePostVerzending } = useField<
  CrmDomainDomainAdviseursDirectePostVerzending | undefined
>('directePostVerzending');
</script>

<template>
  <VContentBox
    title="Instellingen op kantoorniveau"
    v-if="adviseurInstellingenOpgehaald && !adviseurInstellingenVerborgen"
  >
    <div class="flex flex-col gap-6">
      <FadeSlideTransition direction="down" group>
        <form>
          <FieldGroup>
            <FieldRowV2
              label="Standaard premietermijn"
              :label-for="ids['default-premietermijn']"
              :label-in-bold="false"
              tooltip="Nieuwe offertes/berekeningen worden standaard met deze premietermijn berekend. Je kunt dit tijdens het offreren aanpassen."
              :errors="[defaultpremietermijnError]"
              :force-label-text-align-left="true"
            >
              <VSelect
                v-model="defaultPremietermijn"
                :id="ids['default-premietermijn']"
                option-label="label"
                option-value="value"
                :options="defaultPremietermijnOptions"
              />
            </FieldRowV2>

            <FieldRowV2
              label="Prolongatiemail"
              :label-for="ids['prolongatiemail-niet-versturen']"
              :label-in-bold="false"
              tooltip="Elke wordt elke maand een e-mail verstuurd naar jouw kantoor inzake verzending prolongatie-aanhangels en poliswijzigingen aan alle relaties."
              :force-label-text-align-left="true"
            >
              <VToggleSwitch
                :id="ids['prolongatiemail-niet-versturen']"
                v-model:is-active="prolongatiemail"
              />
            </FieldRowV2>

            <FieldRowV2
              label="Voorkeur hoofdvervaldatum"
              :label-for="ids['default-hvv-dag']"
              :label-in-bold="false"
              tooltip="Nieuwe polissen worden standaard opgemaakt met deze hoofdvervaldatum. Je kunt dit tijdens het offreren aanpassen."
              :force-label-text-align-left="true"
            >
              <FloatLabel>
                <VSelect
                  v-model="defaultHvvDag"
                  :id="ids['default-hvv-dag']"
                  option-label="label"
                  option-value="value"
                  :options="hvvDagOptions"
                />

                <label :for="ids['default-hvv-dag']">Hoofdvervaldatum dag</label>
              </FloatLabel>

              <FloatLabel>
                <VSelect
                  v-model="defaultHvvMaand"
                  :id="ids['default-hvv-maand']"
                  option-label="label"
                  option-value="value"
                  :options="hvvMaandOptions"
                />

                <label :for="ids['default-hvv-maand']">Hoofdvervaldatum maand</label>
              </FloatLabel>
            </FieldRowV2>

            <FieldRowV2
              label="Voorkeur polisstukken automatisch mailen"
              :label-for="ids['directe-post-verzending']"
              :label-in-bold="false"
              tooltip="Bij het verzenden van je aanvraag wordt de optie 'polisstukken automatisch mailen' gevuld met de voorkeur die je hier invult."
              :force-label-text-align-left="true"
            >
              <VSelect
                v-model="directePostVerzending"
                :id="ids['directe-post-verzending']"
                option-label="label"
                option-value="value"
                :options="directePostVerzendingOptions"
              />
            </FieldRowV2>

            <FieldRowV2
              label="Documenten"
              :label-in-bold="false"
              tooltip="Deze documenten worden standaard als bijlage toegevoegd bij offerte-emails naar de relatie."
            >
              <KantoorDocumentenField v-if="adviseurNummer" :adviseurnummer="adviseurNummer" />
            </FieldRowV2>
          </FieldGroup>
        </form>
      </FadeSlideTransition>
    </div>
  </VContentBox>
</template>
